import React from 'react';

// Importing icons to match the image exactly (Material-UI v4 compatible)
import TrendingUpIcon from '@material-ui/icons/TrendingUp';
import TrackChangesIcon from '@material-ui/icons/TrackChanges';
import ArchiveIcon from '@material-ui/icons/Archive'; 
import AttachMoneyIcon from '@material-ui/icons/AttachMoney';
import InfoIcon from '@material-ui/icons/Info';
import EmojiObjectsIcon from '@material-ui/icons/EmojiObjects';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';

import AssessmentIcon from '@material-ui/icons/Assessment';
import DomainIcon from '@material-ui/icons/Domain'; 
import LocalDrinkIcon from '@material-ui/icons/LocalDrink';
import LocalShippingIcon from '@material-ui/icons/LocalShipping';
import SettingsIcon from '@material-ui/icons/Settings';
import AccountBalanceIcon from '@material-ui/icons/AccountBalance';

export const SUMMARY_METRICS = [
    { label: 'Latest Demand', value: '12.4M cases', delta: '+0.8M vs operating plan', tone: 'blue', icon: <TrendingUpIcon />, deltaTone: 'good'},
    { label: 'Demand Coverage', value: '98.8%', delta: '+3.8 pp after optimization', tone: 'green', icon: <TrackChangesIcon />, deltaTone: 'good' },
    { label: 'Inventory', value: '28 days', delta: '−4 days opportunity', tone: 'purple', icon: <ArchiveIcon />, deltaTone: 'bad' }, 
    { label: 'Value at Stake', value: '₹82 Cr', delta: 'identified across exceptions', tone: 'orange', icon: <AttachMoneyIcon />, deltaTone: 'warn' },
];

export const DEMAND_TO_CASH_STAGES = [
    { label: 'DEMAND', value: '12.4M', note: '+0.8M vs plan', tone: 'demand', icon: <AssessmentIcon /> },
    { label: 'PRODUCTION', value: '12.25M', note: '0.15M gap', tone: 'production', icon: <DomainIcon /> }, 
    { label: 'BOTTLING', value: '12.10M', note: '2 lines constrained', tone: 'bottling', icon: <LocalDrinkIcon /> },
    { label: 'DEPLOYMENT', value: '11.95M', note: '0.41M reallocate', tone: 'deployment', icon: <LocalShippingIcon /> },
    { label: 'SERVICE', value: '98.8%', note: '+3.8 pp', tone: 'service', icon: <SettingsIcon /> },
    { label: 'CASH', value: '₹50 Cr', note: 'Release potential', tone: 'cash', icon: <AccountBalanceIcon /> },
];

// Updated data to match the image exactly
export const TOP_EXCEPTIONS = [
    { rank: '1', title: 'Demand ↑', detail: 'Premium portfolio • West', value: '+₹18 Cr', tone: 'bad' },
    { rank: '2', title: 'Capacity', detail: 'South bottling line >95%', value: '₹12 Cr risk', tone: 'warn' },
    { rank: '3', title: 'Inventory', detail: 'East excess / West shortage', value: '₹7 Cr risk', tone: 'bad' },
    { rank: '4', title: 'Packaging', detail: '750ml glass constraint', value: '₹24 Cr', tone: 'good' },
    { rank: '5', title: 'Deployment', detail: 'Reallocate 0.41M cases', value: '₹24 Cr', tone: 'good' },
];

export const MANAGEMENT_ACTIONS = [
    { priority: 'HIGH', gap: 'Premium demand above plan in West', intervention: 'Increase North run; redirect available stock', owner: 'S&OP', due: '48 hrs', impact: '₹18 Cr revenue', status: 'In Progress' },
    { priority: 'HIGH', gap: 'South bottling capacity constrained', intervention: 'Shift selected SKUs to available line/site', owner: 'Operations', due: '24 hrs', impact: '₹12 Cr protected', status: 'Not Started' },
    { priority: 'HIGH', gap: 'Stock imbalance across markets', intervention: 'Move excess East inventory to deficit markets', owner: 'Supply', due: '72 hrs', impact: '₹9 Cr / service', status: 'In Progress' },
    { priority: 'MED', gap: '750ml glass below requirement', intervention: 'Advance PO / alternate packaging source', owner: 'Procurement', due: '48 hrs', impact: '₹7 Cr protected', status: 'Not Started' },
    { priority: 'MED', gap: 'Core SKU demand softening in East', intervention: 'Reduce production; avoid excess inventory', owner: 'Planning', due: 'This week', impact: '₹5 Cr inventory', status: 'Not Started' },
];

export class CommandCentre extends React.Component {
    render() {
        return (
            <div className="cc-page">
                {/* Header */}
                <div className="cc-header">
                    <div className="cc-header-text">
                        <h1>Enterprise Performance Command Centre</h1>
                        <p>One view of demand, supply, bottling, deployment and value — focused on the exceptions that need management action.</p>
                    </div>
                    <div className="cc-header-filters">
                        <button className="cc-filter-btn">Next 8 Weeks</button>
                        <button className="cc-filter-btn">All States</button>
                        <button className="cc-filter-btn">All Brands</button>
                    </div>
                </div>

                {/* Metric Cards Row */}
                <div className="cc-metric-row">
                    {SUMMARY_METRICS.map((metric) => (
                        <div className={`cc-metric-card tone-${metric.tone}`} key={metric.label}>
                            <div className="cc-metric-icon-wrap">{metric.icon}</div>
                            <div className="cc-metric-content">
                                <div className="cc-metric-label">{metric.label}</div>
                                <div className="cc-metric-value">{metric.value}</div>
                                <div className={`cc-metric-delta cc-delta-${metric.deltaTone || metric.tone}`}>
                                    {metric.tone !== 'purple' && metric.tone !== 'orange' && <span className="cc-delta-icon">↗</span>}
                                    {metric.delta}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Main Content Row */}
                <div className="cc-main-row">
                    {/* Left Panel: Demand-to-Cash Flow */}
                    <div className="cc-panel cc-flow-panel">
                        <div className="cc-panel-header">
                            <h2>Demand-to-Cash Performance — Where Is the Gap?</h2>
                        </div>
                        <p className="cc-panel-sub">Compare latest demand with plan and immediately see where production, bottling or deployment must change.</p>

                        <div className="cc-flow-row">
                            {DEMAND_TO_CASH_STAGES.map((stage, index) => (
                                <React.Fragment key={stage.label}>
                                    <div className={`cc-flow-card cc-flow-${stage.tone}`}>
                                        <div className="cc-flow-icon">{stage.icon}</div>
                                        <div className="cc-flow-label">{stage.label}</div>
                                        <div className="cc-flow-value">{stage.value}</div>
                                        <div className="cc-flow-note">{stage.note}</div>
                                    </div>
                                    {index < DEMAND_TO_CASH_STAGES.length - 1 && (
                                        <span className="cc-flow-arrow">&#8594;</span>
                                    )}
                                </React.Fragment>
                            ))}
                        </div>

                        <div className="cc-management-question">
                            <EmojiObjectsIcon className="cc-mgmt-icon" />
                            <span><strong>Management question:</strong> What changed vs plan, what action is required now, who owns it, and what ₹ impact will it protect?</span>
                        </div>
                    </div>

                    {/* Right Panel: Exceptions */}
                    <div className="cc-panel cc-exceptions-panel">
                        <div className="cc-panel-header">
                            <h2>Top AI-Prioritized Exceptions</h2>
                        </div>
                        <div className="cc-exceptions-list">
                            {TOP_EXCEPTIONS.map((item) => (
                                <div className="cc-exception-row" key={item.rank}>
                                    <span className={`cc-exception-rank rank-${item.rank}`}>{item.rank}</span>
                                    {/* Wrapped title and detail to stack vertically */}
                                    <div className="cc-exception-text-wrap">
                                        <span className="cc-exception-title">{item.title}</span>
                                        <span className="cc-exception-detail">{item.detail}</span>
                                    </div>
                                    <span className={`cc-exception-value cc-delta-${item.tone}`}>{item.value}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Bottom Panel: Management Actions Table */}
                <div className="cc-panel cc-actions-panel">
                    <h2>Management Actions Triggered by Performance.AI</h2>
                    <p className="cc-panel-sub">From exception detection to cross-functional action — without waiting for the next planning cycle.</p>

                    <table className="cc-actions-table">
                        <thead>
                            <tr>
                                <th>Priority</th>
                                <th>Performance Gap</th>
                                <th>Recommended Intervention</th>
                                <th>Owner</th>
                                <th>Due</th>
                                <th>Expected Impact</th>
                                <th>Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {MANAGEMENT_ACTIONS.map((action) => (
                                <tr key={action.gap}>
                                    <td>
                                        <span className={`cc-priority-tag cc-priority-${action.priority.toLowerCase()}`}>
                                            {action.priority}
                                        </span>
                                    </td>
                                    <td>{action.gap}</td>
                                    <td>{action.intervention}</td>
                                    <td>{action.owner}</td>
                                    <td>{action.due}</td>
                                    <td className="cc-delta-good">{action.impact}</td>
                                    <td>
                                        <span className={`cc-status-tag cc-status-${action.status.toLowerCase().replace(/\s+/g, '-')}`}>
                                            {action.status}
                                        </span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>

                    <p className="cc-footnote">
                        Illustrative UX and values for discussion — intended to show how operating data can be converted into prioritized performance interventions.
                    </p>
                </div>
            </div>
        );
    }
}

export default CommandCentre;